/**
 * Equipment definitions (M7-A / M7-B).
 *
 * Strongly typed item defs vs runtime instances (see domain/equipment.ts).
 * Fixture defs: tests / DEV UI only — excluded from loot tables.
 * Lootable defs: base items for procedural generation (M7-B).
 */

/** Final nine equipment slots (M7-A). No gloves, no amulet. */
export const EQUIPMENT_SLOT_IDS = [
  'helmet',
  'chest',
  'belt',
  'boots',
  'main_hand',
  'off_hand',
  'ring_1',
  'ring_2',
  'special_die',
] as const;

export type EquipmentSlotId = (typeof EQUIPMENT_SLOT_IDS)[number];

export const EQUIPMENT_SLOT_LABELS: Record<EquipmentSlotId, string> = {
  helmet: 'Helmet',
  chest: 'Chest Armour',
  belt: 'Belt',
  boots: 'Boots',
  main_hand: 'Main-Hand',
  off_hand: 'Off-Hand',
  ring_1: 'Ring 1',
  ring_2: 'Ring 2',
  special_die: 'Special Die',
};

/**
 * M7-B locked rarities: Common, Uncommon, Rare, Epic, Legendary.
 * `mythic` kept as legacy/extensible string for old data — generator never rolls it.
 */
export const ITEM_RARITIES = [
  'common',
  'uncommon',
  'rare',
  'epic',
  'legendary',
  'mythic',
] as const;

export type ItemRarity = (typeof ITEM_RARITIES)[number] | (string & {});

/** Inventory filter tabs (Hero screen). */
export const ITEM_FILTERS = [
  'all',
  'weapons',
  'armour',
  'accessories',
  'special_dice',
] as const;

export type ItemFilterId = (typeof ITEM_FILTERS)[number];

export type ItemCategory =
  | 'weapon'
  | 'armour'
  | 'accessory'
  | 'special_die';

export type WeaponHandedness = 'one_hand' | 'two_hand';

/** Weapon families for class permission checks (GDD table). */
export type WeaponFamily =
  | 'sword'
  | 'dagger'
  | 'axe'
  | 'mace'
  | 'wand'
  | 'greatsword'
  | 'greataxe'
  | 'warhammer'
  | 'bow'
  | 'staff'
  | 'scythe';

/**
 * Static item definition.
 * Affix rolls live on ItemInstance; weapon swing uses baseWeaponDamage.
 */
export interface ItemDef {
  id: string;
  name: string;
  category: ItemCategory;
  /** Slots this item may occupy. Rings use both ring_1 and ring_2. */
  allowedSlots: readonly EquipmentSlotId[];
  /** Weapons only. Two-hand disables off-hand while equipped. */
  handedness?: WeaponHandedness;
  /** Weapons — used by class permission table. */
  weaponFamily?: WeaponFamily;
  /** Off-hand shield flag (class permission: shield). */
  isShield?: boolean;
  /** Magical focus flag (class permission: magical focus). */
  isMagicalFocus?: boolean;
  /**
   * DEV_PROVISIONAL weapon swing base (M7-C / M7-D2).
   * Scaled by rarity + itemLevel at combat time. Shields / non-weapons omit.
   */
  baseWeaponDamage?: number;
  /**
   * APPROVED_INITIAL inherent flat Armour (helmet/chest/belt/boots/shield).
   * Scaled by rarity + itemLevel at combat time. BASE_ARMOUR_RATING remains 0.
   */
  baseFlatArmour?: number;
  /**
   * True for test/DEV fixture defs only. Never award via loot gen.
   */
  fixture?: boolean;
  /** Eligible for procedural loot tables. */
  lootable?: boolean;
  /** Default rarity for fixtures / helpers. */
  defaultRarity?: ItemRarity;
}

/** Catalog of known defs (fixtures + lootable bases). */
export const ITEM_DEFS: readonly ItemDef[] = [
  // --- Fixtures (M7-A / screenshots) ---
  {
    id: 'fix_helmet_leather',
    name: 'Leather Cap',
    category: 'armour',
    allowedSlots: ['helmet'],
    baseFlatArmour: 2,
    fixture: true,
    defaultRarity: 'common',
  },
  {
    id: 'fix_chest_mail',
    name: 'Mail Hauberk',
    category: 'armour',
    allowedSlots: ['chest'],
    baseFlatArmour: 4,
    fixture: true,
    defaultRarity: 'common',
  },
  {
    id: 'fix_belt_simple',
    name: 'Simple Belt',
    category: 'armour',
    allowedSlots: ['belt'],
    baseFlatArmour: 2,
    fixture: true,
    defaultRarity: 'common',
  },
  {
    id: 'fix_boots_leather',
    name: 'Leather Boots',
    category: 'armour',
    allowedSlots: ['boots'],
    baseFlatArmour: 2,
    fixture: true,
    defaultRarity: 'common',
  },
  {
    id: 'fix_sword_1h',
    name: 'Practice Sword',
    category: 'weapon',
    allowedSlots: ['main_hand', 'off_hand'],
    handedness: 'one_hand',
    weaponFamily: 'sword',
    /** DEV_PROVISIONAL */
    baseWeaponDamage: 4,
    fixture: true,
    defaultRarity: 'common',
  },
  {
    id: 'fix_greatsword_2h',
    name: 'Practice Greatsword',
    category: 'weapon',
    allowedSlots: ['main_hand'],
    handedness: 'two_hand',
    weaponFamily: 'greatsword',
    /** DEV_PROVISIONAL */
    baseWeaponDamage: 8,
    fixture: true,
    defaultRarity: 'rare',
  },
  {
    id: 'fix_shield_wood',
    name: 'Wooden Shield',
    category: 'weapon',
    allowedSlots: ['off_hand'],
    handedness: 'one_hand',
    isShield: true,
    baseFlatArmour: 3,
    fixture: true,
    defaultRarity: 'common',
  },
  {
    id: 'fix_ring_plain',
    name: 'Plain Ring',
    category: 'accessory',
    allowedSlots: ['ring_1', 'ring_2'],
    fixture: true,
    defaultRarity: 'common',
  },
  {
    id: 'fix_special_die_blank',
    name: 'Blank Special Die',
    category: 'special_die',
    allowedSlots: ['special_die'],
    fixture: true,
    defaultRarity: 'rare',
  },
  // --- Lootable base items (M7-B) ---
  {
    id: 'helm_iron',
    name: 'Iron Helm',
    category: 'armour',
    allowedSlots: ['helmet'],
    baseFlatArmour: 2,
    lootable: true,
    defaultRarity: 'common',
  },
  {
    id: 'chest_iron',
    name: 'Iron Cuirass',
    category: 'armour',
    allowedSlots: ['chest'],
    baseFlatArmour: 4,
    lootable: true,
    defaultRarity: 'common',
  },
  {
    id: 'belt_iron',
    name: 'Iron Belt',
    category: 'armour',
    allowedSlots: ['belt'],
    baseFlatArmour: 2,
    lootable: true,
    defaultRarity: 'common',
  },
  {
    id: 'boots_iron',
    name: 'Iron Greaves',
    category: 'armour',
    allowedSlots: ['boots'],
    baseFlatArmour: 2,
    lootable: true,
    defaultRarity: 'common',
  },
  {
    id: 'sword_iron',
    name: 'Iron Sword',
    category: 'weapon',
    allowedSlots: ['main_hand', 'off_hand'],
    handedness: 'one_hand',
    weaponFamily: 'sword',
    /** DEV_PROVISIONAL */
    baseWeaponDamage: 4,
    lootable: true,
    defaultRarity: 'common',
  },
  {
    id: 'axe_iron',
    name: 'Iron Axe',
    category: 'weapon',
    allowedSlots: ['main_hand', 'off_hand'],
    handedness: 'one_hand',
    weaponFamily: 'axe',
    /** DEV_PROVISIONAL */
    baseWeaponDamage: 5,
    lootable: true,
    defaultRarity: 'common',
  },
  {
    id: 'dagger_iron',
    name: 'Iron Dagger',
    category: 'weapon',
    allowedSlots: ['main_hand', 'off_hand'],
    handedness: 'one_hand',
    weaponFamily: 'dagger',
    /** DEV_PROVISIONAL */
    baseWeaponDamage: 3,
    lootable: true,
    defaultRarity: 'common',
  },
  {
    id: 'greatsword_iron',
    name: 'Iron Greatsword',
    category: 'weapon',
    allowedSlots: ['main_hand'],
    handedness: 'two_hand',
    weaponFamily: 'greatsword',
    /** DEV_PROVISIONAL */
    baseWeaponDamage: 8,
    lootable: true,
    defaultRarity: 'uncommon',
  },
  {
    id: 'shield_iron',
    name: 'Iron Shield',
    category: 'weapon',
    allowedSlots: ['off_hand'],
    handedness: 'one_hand',
    isShield: true,
    baseFlatArmour: 3,
    lootable: true,
    defaultRarity: 'common',
  },
  {
    id: 'wand_oak',
    name: 'Oak Wand',
    category: 'weapon',
    // GDD: any two class-permitted 1H weapons (incl. dual wands) may dual-wield.
    allowedSlots: ['main_hand', 'off_hand'],
    handedness: 'one_hand',
    weaponFamily: 'wand',
    isMagicalFocus: true,
    /** DEV_PROVISIONAL */
    baseWeaponDamage: 3,
    lootable: true,
    defaultRarity: 'uncommon',
  },
  {
    id: 'staff_oak',
    name: 'Oak Staff',
    category: 'weapon',
    allowedSlots: ['main_hand'],
    handedness: 'two_hand',
    weaponFamily: 'staff',
    isMagicalFocus: true,
    /** DEV_PROVISIONAL */
    baseWeaponDamage: 7,
    lootable: true,
    defaultRarity: 'uncommon',
  },
  {
    id: 'ring_copper',
    name: 'Copper Ring',
    category: 'accessory',
    allowedSlots: ['ring_1', 'ring_2'],
    lootable: true,
    defaultRarity: 'common',
  },
  {
    id: 'ring_silver',
    name: 'Silver Ring',
    category: 'accessory',
    allowedSlots: ['ring_1', 'ring_2'],
    lootable: true,
    defaultRarity: 'uncommon',
  },
  {
    id: 'special_die_ember',
    name: 'Ember Special Die',
    category: 'special_die',
    allowedSlots: ['special_die'],
    lootable: true,
    defaultRarity: 'rare',
  },
] as const;

const DEF_BY_ID: ReadonlyMap<string, ItemDef> = new Map(
  ITEM_DEFS.map((d) => [d.id, d]),
);

export function getItemDef(defId: string): ItemDef | undefined {
  return DEF_BY_ID.get(defId);
}

export function isKnownItemDef(defId: string): boolean {
  return DEF_BY_ID.has(defId);
}

export function listLootableDefs(category?: ItemCategory): ItemDef[] {
  return ITEM_DEFS.filter(
    (d) => d.lootable && !d.fixture && (category ? d.category === category : true),
  );
}

export function filterMatchesCategory(
  category: ItemCategory,
  filter: ItemFilterId,
): boolean {
  if (filter === 'all') return true;
  if (filter === 'weapons') return category === 'weapon';
  if (filter === 'armour') return category === 'armour';
  if (filter === 'accessories') return category === 'accessory';
  if (filter === 'special_dice') return category === 'special_die';
  return true;
}

export function createEmptyEquippedSlots(): Record<
  EquipmentSlotId,
  string | null
> {
  return {
    helmet: null,
    chest: null,
    belt: null,
    boots: null,
    main_hand: null,
    off_hand: null,
    ring_1: null,
    ring_2: null,
    special_die: null,
  };
}

export function isEquipmentSlotId(value: string): value is EquipmentSlotId {
  return (EQUIPMENT_SLOT_IDS as readonly string[]).includes(value);
}

export const RARITY_DISPLAY_COLORS: Record<string, string> = {
  common: '#b0b8c8',
  uncommon: '#5ecf7a',
  rare: '#5b9fff',
  epic: '#c07bff',
  legendary: '#ffb347',
  mythic: '#ff6b9d',
};
